import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/label';
import { InlineError } from '@/components/ui/states';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Escribe tu contraseña actual.'),
    newPassword: z
      .string()
      .min(10, 'Usa al menos 10 caracteres.')
      .regex(/[a-z]/, 'Incluye al menos una minúscula.')
      .regex(/[A-Z]/, 'Incluye al menos una mayúscula.')
      .regex(/\d/, 'Incluye al menos un número.'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Las dos contraseñas no coinciden.',
    path: ['confirmPassword'],
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    message: 'La contraseña nueva debe ser distinta de la actual.',
    path: ['newPassword'],
  });

export type PasswordForm = z.infer<typeof passwordSchema>;

export function ChangePasswordForm({
  onDone,
  submitLabel = 'Cambiar contraseña',
}: {
  onDone?: () => void;
  submitLabel?: string;
}) {
  const { changePassword } = useAuth();
  const form = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: PasswordForm) => changePassword(values.currentPassword, values.newPassword),
    onSuccess: () => {
      toast.success('Contraseña actualizada');
      form.reset();
      onDone?.();
    },
  });

  return (
    <form
      id="change-password-form"
      onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
      className="flex flex-col gap-4"
      noValidate
    >
      <InlineError error={mutation.error} />

      <Field
        label="Contraseña actual"
        htmlFor="currentPassword"
        required
        error={form.formState.errors.currentPassword?.message}
      >
        <Input
          id="currentPassword"
          type="password"
          autoComplete="current-password"
          invalid={!!form.formState.errors.currentPassword}
          {...form.register('currentPassword')}
        />
      </Field>

      <Field
        label="Contraseña nueva"
        htmlFor="newPassword"
        required
        hint="Mínimo 10 caracteres, con mayúsculas, minúsculas y números."
        error={form.formState.errors.newPassword?.message}
      >
        <Input
          id="newPassword"
          type="password"
          autoComplete="new-password"
          invalid={!!form.formState.errors.newPassword}
          {...form.register('newPassword')}
        />
      </Field>

      <Field
        label="Repite la contraseña nueva"
        htmlFor="confirmPassword"
        required
        error={form.formState.errors.confirmPassword?.message}
      >
        <Input
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          invalid={!!form.formState.errors.confirmPassword}
          {...form.register('confirmPassword')}
        />
      </Field>

      <Button type="submit" variant="primary" loading={mutation.isPending} className="self-stretch">
        {submitLabel}
      </Button>
    </form>
  );
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Cambiar contraseña</DialogTitle>
          <DialogDescription>
            Al guardar, las demás sesiones abiertas dejarán de ser válidas.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ChangePasswordForm onDone={() => onOpenChange(false)} />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
